/* generated using openapi-typescript-codegen -- do not edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { BatchAnalysisResponse } from '../models/BatchAnalysisResponse';
import type { RecordOutcomeRequest } from '../models/RecordOutcomeRequest';
import type { RecordOutcomeResponse } from '../models/RecordOutcomeResponse';
import type { RollAnalyzerRequest } from '../models/RollAnalyzerRequest';
import type { RollAnalyzerResponse } from '../models/RollAnalyzerResponse';
import type { UpdateWeightsRequest } from '../models/UpdateWeightsRequest';
import type { WeightsResponse } from '../models/WeightsResponse';
import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';
export class RollAnalyzerService {
    /**
     * Analyze Position
     * Analyze an option position for roll vs close decision.
     *
     * Uses ORATS live data to evaluate:
     * - DTE (theta decay acceleration)
     * - IV changes since entry
     * - Current P&L
     * - IV rank (premium environment)
     *
     * Returns a recommendation with confidence score and reasoning.
     *
     * **Decision Framework:**
     * | Factor | Roll Signal | Close Signal |
     * |--------|-------------|--------------|
     * | DTE | < 21 days | < 7 days + loss |
     * | IV Change | Up >10% | Down >20% |
     * | P&L | Profitable (0-50%) | >50% or <-50% |
     * | IV Rank | >70% | <30% |
     *
     * Score >= 2 signals -> ROLL or CLOSE; otherwise HOLD
     * @returns RollAnalyzerResponse Successful Response
     * @throws ApiError
     */
    public static analyzePositionApiV1RollAnalyzerAnalyzePost({
        requestBody,
    }: {
        requestBody: RollAnalyzerRequest,
    }): CancelablePromise<RollAnalyzerResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/roll-analyzer/analyze',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Analyze Positions Batch
     * Analyze all option positions and return alerts + adaptive scores.
     *
     * This is the main Position Review endpoint. It:
     * 1. Fetches all current option positions
     * 2. Calculates normalized 0-100 scores for each factor
     * 3. Applies learned weights per strategy bucket
     * 4. Generates objective alert flags (CRITICAL/WARNING/INFO)
     * 5. Optionally creates decision snapshots for outcome tracking
     *
     * **Sorting**: Positions sorted by urgency:
     * - CRITICAL alerts first
     * - WARNING alerts second
     * - Then by attention score (highest first)
     * - Then by DTE (lowest first)
     *
     * **Strategy Buckets**:
     * - THETA_POSITIVE: Short premium (CSPs, credit spreads) - high theta is good
     * - THETA_NEGATIVE: Long premium (debit spreads, long calls) - high theta is bad
     * - NEUTRAL: Unknown/mixed strategies
     *
     * Args:
     * create_snapshots: If True, creates PositionDecisionSnapshot records for outcome tracking
     *
     * Returns:
     * BatchAnalysisResponse with positions, summary, and weights used
     * @returns BatchAnalysisResponse Successful Response
     * @throws ApiError
     */
    public static analyzePositionsBatchApiV1RollAnalyzerBatchPost({
        createSnapshots = true,
    }: {
        createSnapshots?: boolean,
    }): CancelablePromise<BatchAnalysisResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/roll-analyzer/batch',
            query: {
                'create_snapshots': createSnapshots,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Record Outcome
     * Record the outcome for a position decision snapshot.
     *
     * Call this when a position closes to record its final P&L.
     * This data is used to learn optimal weights over time.
     *
     * **Outcome Classification**:
     * - GOOD: P&L improved or preserved (>= -5% from snapshot)
     * - NEUTRAL: P&L down 5-20% (noise, doesn't inform learning)
     * - BAD: P&L down >20% (valuable signal - we want more warnings)
     *
     * Args:
     * request: Contains snapshot_id and final outcome_pnl_pct
     *
     * Returns:
     * RecordOutcomeResponse with outcome classification and P&L change
     * @returns RecordOutcomeResponse Successful Response
     * @throws ApiError
     */
    public static recordOutcomeApiV1RollAnalyzerRecordOutcomePost({
        requestBody,
    }: {
        requestBody: RecordOutcomeRequest,
    }): CancelablePromise<RecordOutcomeResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/roll-analyzer/record-outcome',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Update Weights
     * Manually trigger weight recalculation from recent outcomes.
     *
     * Weights are learned from rolling 6-month (configurable) outcomes.
     * The algorithm rewards PREDICTIVE SIGNAL - factors that predict
     * extreme outcomes (good OR bad) get higher weights.
     *
     * **Learning Logic**:
     * - Factors that led to GOOD outcomes → higher weight (we want to pay attention)
     * - Factors that led to BAD outcomes → higher weight (we want more warnings)
     * - Factors that led to NEUTRAL outcomes → lower weight (noise)
     *
     * Args:
     * request: Optional config (strategy_bucket, lookback_months)
     *
     * Returns:
     * WeightsResponse with updated weights for all buckets
     * @returns WeightsResponse Successful Response
     * @throws ApiError
     */
    public static updateWeightsApiV1RollAnalyzerUpdateWeightsPost({
        requestBody,
    }: {
        requestBody?: (UpdateWeightsRequest | null),
    }): CancelablePromise<WeightsResponse> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/roll-analyzer/update-weights',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Get Weights
     * Get current factor weights and historical records.
     *
     * Weights determine how much each factor contributes to the
     * attention score. They are learned per strategy bucket.
     *
     * **Default Weights** (before learning):
     * - THETA_POSITIVE: DTE=25, P&L=20, Gamma=15, Theta=5, IV_Rank=15, Earnings=10, Vega/Theta=10
     * - THETA_NEGATIVE: DTE=15, P&L=25, Gamma=10, Theta=20, IV_Rank=15, Earnings=5, Vega/Theta=10
     *
     * Args:
     * strategy_bucket: Optional filter for specific bucket
     *
     * Returns:
     * WeightsResponse with current weights and history
     * @returns WeightsResponse Successful Response
     * @throws ApiError
     */
    public static getWeightsApiV1RollAnalyzerWeightsGet({
        strategyBucket,
    }: {
        strategyBucket?: (string | null),
    }): CancelablePromise<WeightsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/roll-analyzer/weights',
            query: {
                'strategy_bucket': strategyBucket,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
}
